import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { createRestaurantProfile } from '../utils/api';

export default function Register() {
  const { register, login } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantAddress, setRestaurantAddress] = useState('');
  const [restaurantPhone, setRestaurantPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!restaurantName.trim() || !restaurantAddress.trim() || !restaurantPhone.trim()) {
      setError('Please fill all restaurant details');
      return;
    }
    setLoading(true);
    try {
      // 1. Register user with Firebase
      const cred = await register(email, password, name);
      // 2. Get token
      const token = await cred.user.getIdToken();
      // 3. Call backend to create restaurant
      await createRestaurantProfile({
        name: restaurantName,
        address: restaurantAddress,
        phone: restaurantPhone,
        email: email
      }, token);
      setSuccess('Registration successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Email exists. Trying to finalize profile...');
        try {
          const userCredential = await login(email, password);
          const token = await userCredential.user.getIdToken();
          await createRestaurantProfile({
            name: restaurantName,
            address: restaurantAddress,
            phone: restaurantPhone,
            email: email,
          }, token);
          setSuccess('Profile setup successful! Redirecting to login...');
          setTimeout(() => navigate('/login'), 1500);
        } catch (fixErr) {
          console.error('Error fixing user state:', fixErr);
          setError('This email is registered but we could not create the restaurant profile. Please contact support.');
        }
      } else {
        setError(err.message || 'Registration failed');
      }
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen q2o-bg">
      <form onSubmit={handleSubmit} className="q2o-card border border-q2o-purple w-full max-w-md">
        <h2 className="text-3xl font-bold mb-6 text-center text-q2o-purple">Create Account</h2>
        {error && <div className="mb-4 text-red-500 text-sm text-center">{error}</div>}
        {success && <div className="mb-4 text-green-500 text-sm text-center">{success}</div>}
        <div className="mb-4">
          <label className="block mb-1 text-q2o-purple font-semibold">Name</label>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 text-q2o-purple font-semibold">Email</label>
          <input
            type="email"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="mb-4 relative">
          <label className="block mb-1 text-q2o-purple font-semibold">Password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white pr-10"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <span
            className="absolute right-3 top-9 cursor-pointer text-q2o-purple"
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={0}
            role="button"
            aria-label="Toggle password visibility"
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        <div className="mb-6 relative">
          <label className="block mb-1 text-q2o-purple font-semibold">Confirm Password</label>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white pr-10"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
          />
          <span
            className="absolute right-3 top-9 cursor-pointer text-q2o-purple"
            onClick={() => setShowConfirmPassword((v) => !v)}
            tabIndex={0}
            role="button"
            aria-label="Toggle confirm password visibility"
          >
            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        {/* Restaurant Details */}
        <div className="mb-4">
          <label className="block mb-1 text-q2o-purple font-semibold">Restaurant Name</label>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white"
            value={restaurantName}
            onChange={e => setRestaurantName(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label className="block mb-1 text-q2o-purple font-semibold">Restaurant Address</label>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white"
            value={restaurantAddress}
            onChange={e => setRestaurantAddress(e.target.value)}
            required
          />
        </div>
        <div className="mb-6">
          <label className="block mb-1 text-q2o-purple font-semibold">Restaurant Phone</label>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring bg-q2o-card text-white"
            value={restaurantPhone}
            onChange={e => setRestaurantPhone(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          className="w-full btn btn-primary btn-lg"
          disabled={loading}
        >
          {loading ? 'Registering...' : 'Register'}
        </button>
        <div className="mt-4 text-center text-sm">
          Already have an account? <a href="/login" className="text-q2o-purple hover:underline">Login</a>
        </div>
      </form>
    </div>
  );
} 